// ADR-0074 §4 / V105-7-1: verification result ingest — authenticated, bound, replay-proof,
// append-only.
//
// Two authenticated paths call `ingestVerificationResult` AFTER their own authentication:
//   - CI: `authenticateCiWorkspaceToken('verification:write')` → the token's workspace;
//   - collector: the ADR-0055 checks (size, collector row, timestamp, Ed25519, nonce) → the
//     collector row's workspace.
// The workspace is never read from the body. Checks, in ADR order; nothing evidentiary is
// written on any failure:
//   1. body parses against gacpro/verification-result@1 and is canonical JSON (422);
//   2. bundle_id exists IN THE AUTHENTICATED WORKSPACE (else 404, same as unknown);
//   3. bundle_sha256 equals the issued one and every probe id / type / version matches the
//      stored manifest (else 422 `tampered`, plus an audit event by the caller);
//   4. not revoked (409), started_at within [issued_at, expires_at] (409 `bundle_expired`),
//      runner kind allowed for the path and the bundle kind (422);
//   5. replay: (bundle_id, run_nonce) unique (409 `replay`); a CI identity token binds one result (474); CI also needs finished_at within
//      the evidence max age;
//   6. CI path: the runner's result signature, verified by the route-supplied verifier against the
//      workspace's reserved verification identity (owner decision 2026-10-08);
//   7. the SERVER evaluates each probe's stored pass criteria.
// Writes: the canonical body to R2, then one batch: a verification_results row + one
// verification_probe_results row per manifest probe. Results are evidence records only;
// nothing here acts on a customer system (ADR-0017).
//
// Out of scope for V105-7-1: control_checks for standing results (V105-7-5) and the plan-state
// recompute (V105-7-2).
import { generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { BUNDLE_ID, VERIFICATION_RESULT_SCHEMA, canonicalVerificationJson, getVerificationBundle, } from './verification-bundle-service.js';
import { getProbeType } from './verification-probes.js';
import { POSTURE_CHECKS } from '../data/posture-checks.js';
/** Largest result body accepted, checked before the body is read (ADR-0074 §4.1). */
export const MAX_VERIFICATION_RESULT_BYTES = 1024 * 1024;
export const MAX_MEASUREMENTS_BYTES = 4 * 1024;
/** A finished_at this far ahead of receipt is refused (the CI evidence rule, `evidenceMetadata`). */
export const RESULT_FUTURE_SKEW_MS = 60_000;
/**
 * V105-7-7 M-1: the collector / manual path's submission age limit. Both runners push right after a run
 * (a failed push is re-run, never re-sent), so a finished_at older than this is a backdated result: it
 * would let a run "start" inside an expired bundle's window and fabricate observed_at history.
 */
export const COLLECTOR_RESULT_MAX_AGE_MS = 24 * 3_600_000;
/** V105-7-7 M-1: the longest run accepted (finished_at - started_at); a hook_timed drill waits at most 2 x 1 day, plus margin. */
export const MAX_RUN_DURATION_MS = 72 * 3_600_000;
const STATUS = {
    invalid_result: 422, non_canonical: 422, not_found: 404, tampered: 422, bundle_revoked: 409, bundle_expired: 409,
    runner_not_allowed: 422, runner_mismatch: 422, environment_mismatch: 422, runner_outdated: 422, replay: 409, stale_result: 422, measurements_too_large: 422,
    ci_run_mismatch: 422, invalid_result_signature: 422,
};
export class VerificationResultError extends Error {
    code;
    bundle;
    status;
    constructor(code, message, bundle) {
        super(message);
        this.code = code;
        this.bundle = bundle;
        this.name = 'VerificationResultError';
        this.status = STATUS[code];
    }
}
const fail = (code, message, bundle) => {
    throw new VerificationResultError(code, message, bundle);
};
// ── parse ────────────────────────────────────────────────────────────────────────
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
const NONCE = /^[A-Za-z0-9_-]{22,128}$/;
const HEX64 = /^[a-f0-9]{64}$/;
const VERSION = /^\d+\.\d+\.\d+[\w.+-]{0,40}$/;
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const keysWithin = (o, allowed) => Object.keys(o).every((k) => allowed.includes(k));
const isoTime = (v) => {
    if (typeof v !== 'string' || !ISO.test(v))
        return null;
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
};
function jsonDepth(v, d = 0) {
    if (Array.isArray(v))
        return v.reduce((m, x) => Math.max(m, jsonDepth(x, d + 1)), d + 1);
    if (isObj(v))
        return Object.values(v).reduce((m, x) => Math.max(m, jsonDepth(x, d + 1)), d + 1);
    return d;
}
/**
 * Parse a raw result. The body must be canonical JSON (sorted keys, no whitespace) so
 * result_sha256 is the sha256 of exactly the bytes received and the bytes stored.
 */
export async function parseVerificationResult(raw) {
    if (new TextEncoder().encode(raw).byteLength > MAX_VERIFICATION_RESULT_BYTES)
        fail('invalid_result', 'result body exceeds 1 MiB');
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return fail('invalid_result', 'result body is not JSON');
    }
    if (!isObj(parsed))
        fail('invalid_result', 'result body must be an object');
    const b = parsed;
    const top = ['schema', 'bundle_id', 'bundle_sha256', 'run_nonce', 'runner', 'started_at', 'finished_at', 'probes', 'hooks', 'ci_run_url'];
    if (!keysWithin(b, top))
        fail('invalid_result', 'unknown top-level field');
    if (b.schema !== undefined && b.schema !== VERIFICATION_RESULT_SCHEMA)
        fail('invalid_result', `schema must be ${VERIFICATION_RESULT_SCHEMA}`);
    if (typeof b.bundle_id !== 'string' || !BUNDLE_ID.test(b.bundle_id))
        fail('invalid_result', 'invalid bundle_id');
    if (typeof b.bundle_sha256 !== 'string' || !HEX64.test(b.bundle_sha256))
        fail('invalid_result', 'invalid bundle_sha256');
    if (typeof b.run_nonce !== 'string' || !NONCE.test(b.run_nonce))
        fail('invalid_result', 'run_nonce must be at least 16 random bytes, base64url');
    const r = b.runner;
    if (!isObj(r) || !keysWithin(r, ['kind', 'version', 'environment_asserted'])
        || !['gac_action', 'collector', 'manual'].includes(String(r.kind))
        || typeof r.version !== 'string' || !VERSION.test(r.version)
        || (r.environment_asserted !== 'sandbox' && r.environment_asserted !== 'target'))
        fail('invalid_result', 'runner is { kind, version, environment_asserted }');
    const started = isoTime(b.started_at);
    const finished = isoTime(b.finished_at);
    if (started === null || finished === null)
        fail('invalid_result', 'started_at / finished_at must be ISO-8601 UTC timestamps');
    if (finished < started)
        fail('invalid_result', 'finished_at is before started_at');
    if (finished - started > MAX_RUN_DURATION_MS)
        fail('invalid_result', 'a run lasts at most 72 hours (finished_at - started_at)');
    if (!Array.isArray(b.probes) || b.probes.length > 1000)
        fail('invalid_result', 'probes must be an array (at most 1000)');
    for (const p of b.probes) {
        if (!isObj(p) || !keysWithin(p, ['probe_id', 'type', 'type_version', 'measurements', 'runner_status', 'error'])
            || typeof p.probe_id !== 'string' || p.probe_id.length > 64 || typeof p.type !== 'string' || p.type.length > 64
            || typeof p.type_version !== 'number' || !Number.isInteger(p.type_version)
            || !isObj(p.measurements) || jsonDepth(p.measurements) > 4
            || !['pass', 'fail', 'error', 'unsupported'].includes(String(p.runner_status))
            || (p.error !== undefined && (typeof p.error !== 'string' || p.error.length > 500)))
            fail('invalid_result', 'malformed probe result');
    }
    if (!Array.isArray(b.hooks) || b.hooks.length > 3)
        fail('invalid_result', 'hooks must be an array (at most 3)');
    for (const h of b.hooks) {
        if (!isObj(h) || !keysWithin(h, ['slot', 'exit_code', 'duration_ms', 'report_sha256'])
            || !['e2e', 'rollback', 'migrate'].includes(String(h.slot))
            || typeof h.exit_code !== 'number' || !Number.isInteger(h.exit_code) || h.exit_code < -1 || h.exit_code > 255
            || typeof h.duration_ms !== 'number' || !Number.isInteger(h.duration_ms) || h.duration_ms < 0
            || (h.report_sha256 !== undefined && (typeof h.report_sha256 !== 'string' || !HEX64.test(h.report_sha256))))
            fail('invalid_result', 'malformed hook result');
    }
    if (b.ci_run_url !== undefined) {
        let u = null;
        try {
            u = typeof b.ci_run_url === 'string' && b.ci_run_url.length <= 2048 ? new URL(b.ci_run_url) : null;
        }
        catch {
            u = null;
        }
        if (!u || u.protocol !== 'https:' || u.username || u.password)
            fail('invalid_result', 'ci_run_url must be an https URL');
    }
    let canonical;
    try {
        canonical = canonicalVerificationJson(b);
    }
    catch {
        return fail('invalid_result', 'result body holds a non-JSON value');
    }
    if (canonical !== raw)
        fail('non_canonical', 'the result body must be canonical JSON (sorted keys, no whitespace)');
    return { body: b, result_sha256: await sha256Hex(raw) };
}
export const resultR2Key = (workspaceId, bundleId, resultId) => `verification/${workspaceId}/${bundleId}/${resultId}.json`;
function versionLess(a, b) {
    const pa = a.split(/[.+-]/).slice(0, 3).map((n) => Number.parseInt(n, 10) || 0);
    const pb = b.split(/[.+-]/).slice(0, 3).map((n) => Number.parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++)
        if (pa[i] !== pb[i])
            return pa[i] < pb[i];
    return false;
}
/** What failed in one probe, from the stored criteria and the measurements (never throws). */
export function explainProbe(issued, measurements) {
    const def = getProbeType(issued.type);
    if (!def?.explain || !def.versions.includes(issued.type_version))
        return [];
    try {
        return def.explain(issued.params, issued.pass, measurements).slice(0, 50).map((d) => d.slice(0, 300));
    }
    catch {
        return [];
    }
}
function runnerKindFor(auth) {
    if (auth.path === 'ci')
        return 'gac_action';
    return auth.purpose === 'verification_runner' ? 'manual' : 'collector';
}
function provenanceFor(auth, signedIdentity) {
    if (auth.path === 'collector')
        return 'identity_verified';
    if (signedIdentity)
        return 'verified';
    return auth.ci_identity ? 'identity_verified' : 'unverified';
}
/** error > fail > partial > pass (any non-pass never advances a plan). */
function deriveOutcome(statuses) {
    if (statuses.some((s) => s === 'error' || s === 'unsupported'))
        return 'error';
    if (statuses.some((s) => s === 'fail'))
        return 'fail';
    if (statuses.some((s) => s === 'not_run'))
        return 'partial';
    return 'pass';
}
function isUniqueViolation(e) {
    const msg = e instanceof Error ? e.message : String(e);
    return /UNIQUE constraint failed|duplicate key value|unique constraint/i.test(msg);
}
export async function ingestVerificationResult(tenant, storage, input) {
    const { auth } = input;
    const now = input.now ?? new Date();
    // 1. schema + canonical form.
    const { body, result_sha256 } = await parseVerificationResult(input.raw);
    // 2. the bundle, in the authenticated workspace only.
    const bundle = await getVerificationBundle(tenant, auth.workspace_id, body.bundle_id);
    if (!bundle)
        return fail('not_found', 'not_found');
    const manifest = JSON.parse(bundle.manifest_json);
    // 3. bound to the issued content: digest + exact probe set.
    if (body.bundle_sha256 !== bundle.bundle_sha256)
        fail('tampered', 'bundle_sha256 does not match the issued bundle', bundle);
    const byId = new Map(manifest.probes.map((p) => [p.probe_id, p]));
    const seen = new Set();
    for (const p of body.probes) {
        const issued = byId.get(p.probe_id);
        if (!issued || seen.has(p.probe_id))
            fail('tampered', `probe ${p.probe_id.slice(0, 64)} is not in the issued bundle`, bundle);
        if (issued.type !== p.type || issued.type_version !== p.type_version)
            fail('tampered', `probe ${p.probe_id} type does not match the issued bundle`, bundle);
        seen.add(p.probe_id);
    }
    const hookSlots = new Set(manifest.hooks.map((h) => h.slot));
    const seenHooks = new Set();
    for (const h of body.hooks) {
        if (!hookSlots.has(h.slot) || seenHooks.has(h.slot))
            fail('tampered', `hook ${h.slot} is not in the issued bundle`, bundle);
        seenHooks.add(h.slot);
    }
    // 4. revoked / expired / runner allowed.
    if (bundle.revoked_at)
        fail('bundle_revoked', 'the bundle has been revoked', bundle);
    const started = Date.parse(body.started_at);
    if (started < Date.parse(bundle.issued_at) || started > Date.parse(bundle.expires_at))
        fail('bundle_expired', 'the run started outside the bundle validity window', bundle);
    const kind = runnerKindFor(auth);
    if (body.runner.kind !== kind)
        fail('runner_mismatch', `this path accepts results from a ${kind} runner`, bundle);
    if (kind === 'collector' && (bundle.bundle_kind !== 'conformance' || body.hooks.length > 0)) {
        fail('runner_not_allowed', 'the customer-run collector runs conformance bundles only, without hooks', bundle);
    }
    // ADR-0074 §1 runner_min_version: the runner refuses to start below it; the server refuses the result too (T8).
    if (versionLess(body.runner.version, manifest.runner_min_version))
        fail('runner_outdated', `this bundle needs runner ${manifest.runner_min_version} or later`, bundle);
    if (body.runner.environment_asserted !== bundle.environment)
        fail('environment_mismatch', `the bundle runs in the ${bundle.environment} environment`, bundle);
    if (auth.path === 'collector' && body.ci_run_url !== undefined)
        fail('invalid_result', 'ci_run_url is only accepted from CI', bundle);
    if (auth.path === 'ci' && auth.ci_run_url && body.ci_run_url !== undefined && body.ci_run_url !== auth.ci_run_url) {
        fail('ci_run_mismatch', 'ci_run_url does not match the verified CI identity', bundle);
    }
    // 5. replay + freshness.
    const finished = Date.parse(body.finished_at);
    const skew = auth.path === 'ci' ? RESULT_FUTURE_SKEW_MS : auth.clock_skew_ms;
    if (finished > now.getTime() + skew)
        fail('stale_result', 'finished_at is in the future', bundle);
    if (auth.path === 'ci' && now.getTime() - finished > auth.max_age_seconds * 1000)
        fail('stale_result', 'the result exceeds the submission age limit', bundle);
    // V105-7-7 M-1: the collector path is bound to receipt time too (no backdated runs, no fabricated history).
    if (auth.path === 'collector' && now.getTime() - finished > COLLECTOR_RESULT_MAX_AGE_MS)
        fail('stale_result', 'the result exceeds the submission age limit (24 hours)', bundle);
    const dup = await tenant.query(`SELECT COUNT(*) AS n FROM verification_results WHERE bundle_id = ? AND run_nonce = ? AND workspace_id = ?`, [bundle.bundle_id, body.run_nonce, auth.workspace_id]);
    if ((dup.results[0]?.n ?? 0) > 0)
        fail('replay', 'this run was already recorded', bundle);
    const identityTokenKey = auth.path === 'ci' ? (auth.identity_token_key ?? null) : null;
    if (identityTokenKey !== null && !/^[a-f0-9]{64}$/.test(identityTokenKey))
        throw new Error('identity_token_key must be a sha256 hex digest');
    if (identityTokenKey !== null) {
        const used = await tenant.query(`SELECT COUNT(*) AS n FROM verification_results WHERE workspace_id = ? AND ci_identity_token_key = ?`, [auth.workspace_id, identityTokenKey]);
        if ((used.results[0]?.n ?? 0) > 0)
            fail('replay', 'this CI identity token was already used for a result; request a new one for each run', bundle);
    }
    // 6. the runner's result signature (CI path), bound to this workspace, bundle and run.
    let signedIdentity = null;
    if (auth.path === 'ci' && auth.signature) {
        try {
            signedIdentity = await auth.signature.verify({
                subject_name: `verification-result:${bundle.bundle_id}`, digest: result_sha256,
                claims: { workspace_id: auth.workspace_id, bundle_id: bundle.bundle_id, bundle_sha256: bundle.bundle_sha256, run_nonce: body.run_nonce },
            });
        }
        catch {
            fail('invalid_result_signature', 'the result signature does not verify for this workspace, bundle and run', bundle);
        }
    }
    // 7. server-side evaluation from the STORED manifest.
    const reported = new Map(body.probes.map((p) => [p.probe_id, p]));
    const rows = [];
    for (const issued of manifest.probes) {
        const rep = reported.get(issued.probe_id);
        let status;
        let measurements = '{}';
        if (!rep) {
            status = 'not_run';
        }
        else {
            measurements = canonicalVerificationJson(rep.measurements);
            if (new TextEncoder().encode(measurements).byteLength > MAX_MEASUREMENTS_BYTES)
                fail('measurements_too_large', `probe ${issued.probe_id}: measurements exceed 4 KB`, bundle);
            const def = getProbeType(issued.type);
            if (!def || !def.versions.includes(issued.type_version) || rep.runner_status === 'unsupported')
                status = 'unsupported';
            else
                status = def.evaluate(issued.params, issued.pass, rep.measurements);
        }
        const details = rep && (status === 'fail' || status === 'error') ? explainProbe(issued, rep.measurements) : [];
        rows.push({ probe_id: issued.probe_id, type: issued.type, type_version: issued.type_version, status, measurements_json: measurements, test_key: issued.test_key ?? null, details });
    }
    const outcome = deriveOutcome(rows.map((r) => r.status));
    const provenance = provenanceFor(auth, signedIdentity);
    // V105-7-7 addendum: which workspace CI identity this lock-eligibility rests on (the signature's when
    // the run is `verified`, else the OIDC token's); null = operator list, collector or unverified.
    const trustIdentityId = auth.path !== 'ci' || provenance === 'unverified' ? null
        : provenance === 'verified' && signedIdentity ? (auth.signature?.trustIdentityId?.(signedIdentity) ?? null)
            : (auth.trust_identity_id ?? null);
    // Writes: body to R2, then one batch (append-only rows).
    const resultId = (input.newId ?? (() => generateRandomId('vr')))();
    const r2Key = resultR2Key(auth.workspace_id, bundle.bundle_id, resultId);
    await storage.put(r2Key, input.raw, { contentType: 'application/json' });
    const signatureKey = auth.path === 'ci' && auth.signature && signedIdentity ? `${r2Key.slice(0, -".json".length)}.sigstore.json` : null;
    if (signatureKey && auth.path === 'ci' && auth.signature)
        await storage.put(signatureKey, auth.signature.json, { contentType: 'application/json' });
    const ciRunUrl = auth.path === 'ci' ? (auth.ci_run_url ?? body.ci_run_url ?? null) : null;
    const runnerIdentity = auth.path === 'ci'
        ? (signedIdentity ?? auth.ci_identity ?? null)
        : `collector:${auth.collector_id}@${body.runner.version}`;
    // V105-7-5 / ADR-0074 §6: a lock-eligible result on a STANDING conformance bundle becomes machine
    // evidence through control_checks (runMachineValidation judges it on its own cadence).
    // V105-7-7 L-2: machine evidence links only a run URL derived from the VERIFIED CI identity, never the body's.
    const standing = await standingChecks(tenant, input.content ?? null, {
        auth, bundle, manifest, rows, provenance, result_sha256, r2Key, runnerIdentity, ciRunUrl: auth.path === 'ci' ? (auth.ci_run_url ?? null) : null,
        observed_at: new Date(finished).toISOString(), now, owner: input.connector_owner_user_id ?? null, runner_kind: kind, runner_version: body.runner.version, resultId,
    });
    try {
        await tenant.batch([
            {
                sql: `INSERT INTO verification_results (result_id, workspace_id, bundle_id, bundle_sha256, run_nonce, runner_kind, runner_version,
                runner_identity, collector_id, ci_token_id, environment_asserted, provenance_level, outcome, result_sha256, result_r2_key,
                signature_r2_key, ci_run_url, started_at, finished_at, received_at, provenance_reason, claimed_identity_json, trust_identity_id, ci_identity_token_key)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [resultId, auth.workspace_id, bundle.bundle_id, bundle.bundle_sha256, body.run_nonce, kind, body.runner.version,
                    runnerIdentity, auth.path === 'collector' ? auth.collector_id : null, auth.path === 'ci' ? auth.token_id : null,
                    body.runner.environment_asserted, provenance, outcome, result_sha256, r2Key, signatureKey, ciRunUrl,
                    new Date(started).toISOString(), new Date(finished).toISOString(), now.toISOString(),
                    auth.path === 'ci' && provenance === 'unverified' ? (auth.provenance_reason ?? null) : null,
                    auth.path === 'ci' && provenance === 'unverified' && auth.claimed_identity ? JSON.stringify(auth.claimed_identity) : null,
                    trustIdentityId, identityTokenKey],
            },
            ...standing.statements,
            ...rows.map((r) => ({
                sql: `INSERT INTO verification_probe_results (result_id, probe_id, probe_type, type_version, status, measurements_json, test_key, check_id)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [resultId, r.probe_id, r.type, r.type_version, r.status, r.measurements_json, r.test_key, standing.checkIdOf.get(r.probe_id) ?? null],
            })),
        ]);
    }
    catch (e) {
        // A concurrent submission of the same run lost the race on UNIQUE (bundle_id, run_nonce), or of the same
        // identity token on UNIQUE (workspace_id, ci_identity_token_key) (474).
        if (isUniqueViolation(e))
            fail('replay', 'this run was already recorded', bundle);
        throw e;
    }
    return {
        result_id: resultId, bundle_id: bundle.bundle_id, bundle_kind: bundle.bundle_kind, plan_id: bundle.plan_id, outcome, provenance_level: provenance, result_sha256, checks_written: standing.statements.length,
        provenance_reason: auth.path === 'ci' && provenance === 'unverified' ? (auth.provenance_reason ?? null) : null,
        probes: rows.map((r) => ({ probe_id: r.probe_id, status: r.status, ...(r.details.length ? { details: r.details } : {}) })),
    };
}
/** A plan's results in THIS workspace, newest first, each probe with what failed. */
export async function listVerificationResults(tenant, workspaceId, planId, limit = 50) {
    const n = Math.min(Math.max(Math.trunc(limit), 1), 200);
    const rows = (await tenant.query(`SELECT r.result_id, r.bundle_id, b.bundle_kind, b.manifest_json, r.runner_kind, r.runner_identity, r.provenance_level, r.outcome, r.environment_asserted,
            r.ci_run_url, r.started_at, r.finished_at, r.received_at
       FROM verification_results r JOIN verification_bundles b ON b.bundle_id = r.bundle_id AND b.workspace_id = r.workspace_id
      WHERE r.workspace_id = ? AND b.plan_id = ? ORDER BY r.received_at DESC, r.result_id DESC LIMIT ${n}`, [workspaceId, planId])).results;
    const out = [];
    for (const r of rows) {
        const manifest = JSON.parse(r.manifest_json);
        const byId = new Map(manifest.probes.map((p) => [p.probe_id, p]));
        const probes = (await tenant.query(`SELECT probe_id, probe_type, status, test_key, measurements_json FROM verification_probe_results WHERE result_id = ? ORDER BY probe_id`, [r.result_id])).results;
        const { manifest_json: _m, ...rest } = r;
        void _m;
        out.push({
            ...rest, lock_eligible: r.provenance_level !== 'unverified',
            probes: probes.map((p) => {
                const issued = byId.get(p.probe_id);
                const details = issued && (p.status === 'fail' || p.status === 'error') ? explainProbe(issued, JSON.parse(p.measurements_json)) : [];
                return { probe_id: p.probe_id, probe_type: p.probe_type, status: p.status, test_key: p.test_key, ...(details.length ? { details } : {}) };
            }),
        });
    }
    return out;
}
// ── V105-7-5: persistent validation (standing conformance → control_checks) ─────────
const POSTURE_CONTROL = new Map(POSTURE_CHECKS.map((c) => [c.check_key, c.control_id]));
/** The ADR-0074 §6 connector kind; one row per runner identity, created on the first standing result. */
export const VERIFICATION_RUNNER_KIND = 'verification_runner';
async function ensureRunnerConnector(tenant, input) {
    const { auth } = input;
    if (auth.path === 'collector') {
        const r = (await tenant.query(`SELECT connector_id FROM evidence_connectors WHERE workspace_id = ? AND kind = ? AND collector_id = ? LIMIT 1`, [auth.workspace_id, VERIFICATION_RUNNER_KIND, auth.collector_id])).results[0];
        if (r)
            return r.connector_id;
    }
    else {
        const r = (await tenant.query(`SELECT connector_id FROM evidence_connectors WHERE workspace_id = ? AND kind = ? AND collector_id IS NULL AND config_json = ? LIMIT 1`, [auth.workspace_id, VERIFICATION_RUNNER_KIND, JSON.stringify({ runner_identity: input.identity })])).results[0];
        if (r)
            return r.connector_id;
    }
    const id = generateRandomId('conn');
    await tenant.execute(`INSERT INTO evidence_connectors (connector_id, workspace_id, kind, status, auth_kind, boundary_declaration, scopes_json, config_json,
       configured_by_user_id, configured_at, provider_key, access_channel, collector_id)
     VALUES (?, ?, ?, 'active', ?, 'metadata_only', '[]', ?, ?, ?, 'gacpro', 'direct', ?)`, [id, auth.workspace_id, VERIFICATION_RUNNER_KIND, auth.path === 'ci' ? 'api_token' : 'collector_key',
        JSON.stringify({ runner_identity: input.identity }), input.owner, input.now.toISOString(), auth.path === 'collector' ? auth.collector_id : null]);
    return id;
}
async function standingChecks(tenant, content, x) {
    const out = { statements: [], checkIdOf: new Map() };
    // §6: only standing conformance; §4: only lock-eligible provenance; target evidence never credits the boundary.
    if (x.bundle.scope !== 'standing' || x.bundle.bundle_kind !== 'conformance' || x.provenance === 'unverified' || !x.owner || !x.runnerIdentity)
        return out;
    const byId = new Map(x.manifest.probes.map((p) => [p.probe_id, p]));
    const eligible = x.rows.filter((r) => r.test_key && (r.status === 'pass' || r.status === 'fail' || r.status === 'error'));
    if (!eligible.length)
        return out;
    // The control a test's evidence lands on: the posture check's control (as the collector writes it), else the reviewed test's control.
    const testControl = new Map();
    for (const key of new Set(eligible.map((r) => r.test_key))) {
        const row = content ? (await content.query(`SELECT control_id FROM assessment_tests WHERE test_key = ? LIMIT 1`, [key])).results[0] : undefined;
        testControl.set(key, row?.control_id ?? null);
    }
    const connectorId = await ensureRunnerConnector(tenant, { auth: x.auth, identity: x.runnerIdentity, owner: x.owner, now: x.now });
    for (const r of eligible) {
        const issued = byId.get(r.probe_id);
        const checkKey = issued.type === 'ksi_check' ? String(issued.params.check_key) : null;
        const controlId = (checkKey ? POSTURE_CONTROL.get(checkKey) : null) ?? testControl.get(r.test_key) ?? null;
        if (!controlId)
            continue;
        const checkId = generateRandomId('cck');
        out.checkIdOf.set(r.probe_id, checkId);
        out.statements.push({
            sql: `INSERT INTO control_checks (check_id, workspace_id, connector_id, control_id, status, evidence_hash, evidence_r2_key, collector_version, observed_at,
              emitter_identity, emitter_signature_verified, submission_timestamp, ci_run_url, provenance_level, details_json, test_key, reason_code)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?, ?)`,
            params: [checkId, x.auth.workspace_id, connectorId, controlId, r.status, x.result_sha256, x.r2Key,
                `verification-runner/${x.runner_kind}@${x.runner_version}`, x.observed_at,
                x.auth.path === 'ci' ? `ci:${x.runnerIdentity}` : x.runnerIdentity, x.now.toISOString(), x.ciRunUrl, x.provenance,
                JSON.stringify({ verification_result_id: x.resultId, bundle_id: x.bundle.bundle_id, probe_id: r.probe_id, probe_type: issued.type, ...(checkKey ? { check_key: checkKey } : {}), measurements: JSON.parse(r.measurements_json) }),
                // V109-1-10 (LM-9): a probe that errored is Couldn't check · collector error.
                r.test_key, r.status === 'error' ? 'collector_error' : null],
        });
    }
    return out;
}
//# sourceMappingURL=verification-result-service.js.map